import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Typography, Grid, Divider, Box, MenuItem, Stack } from '@mui/material';
import moment from 'moment-timezone';
import { useFormikContext } from 'formik';
import { Theme, styled } from '~/themes';
import { TIMEZONE_SETTING } from '~/constants/adminOptions';
import { FastField, Field } from '~/components/common/Formik';
import {
  CHANGE_PAGE_OPTION,
  DEFAULT_TIMEZONE,
  REPEAT_NOTIFICATION_OPTION,
} from '~/constants/common';
import { IAccount } from '~/models/adminOptions';
import { ITimezone } from '~/models/common';
import { formatSimpleDateTime, USER_DEVICE_ZONE } from '~/utils/common';
import {
  useGetNotificationSound,
  useRemoveUserAvatar,
  useUpdateUserAvatar,
} from '~/services/api/shared';
import Button from '~/components/common/Button';
import { setAccountQueryData } from '~/utils/reactQuery';
import {
  UploadImageDialog,
  UploadImageProvider,
  UploadImageSelection,
} from '~/components/shared/UploadImage/';
import LockIcon from '~/assets/images/icons/lock.svg';
import { getTimeZoneNameAbbr } from '~/utils/formatter';
import { convertTimezoneOption } from '~/utils/settings';
import SwitchButton from '~/components/common/SwitchButton';
import PlayAudioButton from './PlayAudioButton';
import ChangePwdModal from './ChangePwdModal';

interface ILocalTime {
  timezone: string;
}

const LocalTime: React.FC<ILocalTime> = ({ timezone }) => {
  const [localTime, setLocalTime] = useState(moment());

  useEffect(() => {
    const interval = setInterval(() => {
      setLocalTime(moment());
    }, 1000);
    return () => {
      clearInterval(interval);
    };
  }, []);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
      }}
    >
      <div>
        <b>Local time is</b>
        <br />
        {timezone
          ? formatSimpleDateTime(
              localTime.clone().tz(timezone.replace('___', '')),
              {
                sameYear: 'MMM D [at] h:mm:ss a',
                differentYear: 'MMM D, YYYY [at] h:mm:ss a',
              },
            )
          : ''}
      </div>
      <div
        style={{
          textAlign: 'right',
        }}
      >
        <b>Universal time is</b>
        <br />
        {formatSimpleDateTime(localTime.utc(), {
          sameYear: 'MMM D [at] h:mm:ss a',
          differentYear: 'MMM D, YYYY [at] h:mm:ss a',
        })}
      </div>
    </Box>
  );
};

const ContentContainer = styled('div')(({ theme }: { theme: Theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  gap: theme.spacing(1.6),
  color: theme.color.violet500,
  maxWidth: '824px',
  width: '100%',
  margin: 'auto',
}));

const Content = styled('div')(({ theme }: { theme: Theme }) => ({
  backgroundColor: 'white',
  borderRadius: theme.spacing(0.8),
  border: '1px solid #E2E7F0',
  padding: theme.spacing(2.4),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: theme.spacing(1.6),
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(1.6),
  },
}));

const Title = styled(Typography)(({ theme }: { theme: Theme }) => ({
  color: theme.color.violet500,
  fontSize: 18,
  fontWeight: 500,
}));

const TextTitle = styled(Typography)(({ theme }: { theme: Theme }) => ({
  color: theme.color.black,
  fontSize: 14,
}));

const RadioContainer = styled(Field.GroupRadioButton)(
  ({ theme }: { theme: Theme }) => ({
    'width': '100%',
    '& .MuiFormGroup-root': {
      gap: theme.spacing(1.6),
      label: {
        padding: 0,
      },
    },
  }),
);

const LocalTimeContainer = styled('div')(({ theme }: { theme: Theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  fontSize: 14,
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(0.5),
}));

const RadioHighlight = styled('div')(() => ({
  fontWeight: 300,
}));

const RadioContent = styled('div')(() => ({
  width: 'calc(100% + 32px)',
  marginLeft: -32,
  marginTop: 20,
}));

interface IProps {
  dataTimezone: ITimezone[];
  isLoadingTimezone: boolean;
}

export const GeneralContent: React.FC<IProps> = ({
  dataTimezone,
  isLoadingTimezone,
}) => {
  const [isChangingPwd, setIsChangingPwd] = useState(false);
  const { values, setFieldValue } = useFormikContext<IAccount>();

  const { data: sounds } = useGetNotificationSound();

  const soundOptions = useMemo(
    () =>
      sounds
        ?.map((sound) => ({
          label: sound?.name,
          value: sound.url,
        }))
        ?.sort((a, b) => {
          const aIsDefault = a.label?.toLowerCase().includes('(default)');
          const bIsDefault = b.label?.toLowerCase().includes('(default)');
          if (aIsDefault && !bIsDefault) return -1;
          if (!aIsDefault && bIsDefault) return 1;
          return 0;
        }) || [],
    [sounds],
  );

  const { mutate: uploadImage, isLoading: isLoadingUploadAvatar } =
    useUpdateUserAvatar({
      onSuccess: () => {},
    });

  const { mutate: removeImage, isLoading: isLoadingRemoveAvatar } =
    useRemoveUserAvatar({
      onSuccess: (resp: IAccount) => {
        setFieldValue('avatar', '');
        setAccountQueryData({ avatar: '', org_id: resp.org_id });
      },
    });

  const autoTimeZone = useMemo(() => {
    if (!dataTimezone) return '';
    const timeZone = getTimeZoneNameAbbr(USER_DEVICE_ZONE, false);
    const tzString = moment().tz(USER_DEVICE_ZONE).format('Z');
    return `${timeZone} (UTC${tzString})`;
  }, [dataTimezone]);

  const timezoneOptions = useMemo(() => {
    if (!dataTimezone) return [];
    return dataTimezone.map(convertTimezoneOption);
  }, [dataTimezone]);

  const onSaveImage = useCallback(async (formData: FormData | null) => {
    if (formData) {
      uploadImage({ body: formData });
    } else {
      removeImage({
        body: {
          data: null,
        },
      });
    }
  }, []);

  return (
    <ContentContainer>
      <Content>
        <Title>Account Information</Title>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '5px',
          }}
        >
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 500,
            }}
          >
            Profile picture
          </Typography>
          <UploadImageProvider
            defaultImages={[values.avatar].filter((item) => !!item)}
            onSaveImage={onSaveImage}
          >
            <UploadImageSelection
              hasIconUpload
              rounded
              size={40}
              loading={isLoadingUploadAvatar || isLoadingRemoveAvatar}
            />
            <UploadImageDialog />
          </UploadImageProvider>
        </Box>
        <Grid container spacing={1.6}>
          <Grid item xs={6}>
            <Field.TextInput name='display_name' legend='Name' />
          </Grid>
          <Grid item xs={6}>
            <Field.TextInput name='email' legend='Email' disabled />
          </Grid>
        </Grid>
        <Button
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
          }}
          buttonType='text'
          onClick={() => setIsChangingPwd(true)}
        >
          <img src={LockIcon} alt='icon-lock' />
          Change Password
        </Button>
      </Content>
      <Content>
        <Title>Notification Settings</Title>
        <Grid container spacing={1.6}>
          <Grid item xs={12} md={4}>
            <Typography>Notify me when</Typography>
          </Grid>
          <Grid item xs={12} md={8}>
            <Grid container>
              <Grid item xs={12} md={6}>
                <Stack flexDirection='row' alignItems='center' mb={1.6}>
                  <FastField.SwitchButton
                    noText
                    name='notification_setting.task_update.enable'
                  />
                  <Typography>Task is updated</Typography>
                </Stack>
              </Grid>
              <Grid item xs={12} md={6}>
                <Stack flexDirection='row' alignItems='center' mb={1.6}>
                  <FastField.SwitchButton
                    noText
                    name='notification_setting.import_csv.enable'
                  />
                  <Typography>CSV import is completed</Typography>
                </Stack>
              </Grid>
            </Grid>
            <Grid item xs={12} md={6}>
              <Stack flexDirection='row' alignItems='center' mb={0.5}>
                <FastField.SwitchButton
                  noText
                  name='notification_setting.new_driver.enable'
                />
                <Typography>New driver joined</Typography>
              </Stack>
            </Grid>
          </Grid>
        </Grid>
        <Divider sx={{ borderColor: '#E2E7F0', width: '100%' }} />
        <Grid container spacing={1.6} alignItems='center'>
          <Grid item xs={12} md={4}>
            <Typography>Notification sound</Typography>
          </Grid>
          <Grid item xs={12} md={8}>
            <Grid container spacing={0.8} alignItems='center'>
              <Grid item xs={12} md={2}>
                <Stack flexDirection='row' alignItems='center'>
                  <SwitchButton
                    noText
                    checked={!values?.notification_setting?.mute}
                    onChange={(e) =>
                      setFieldValue(
                        'notification_setting.mute',
                        !e.target.checked,
                      )
                    }
                  />
                  <Typography>
                    {values?.notification_setting?.mute ? 'Off' : 'On'}
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={6} md={4}>
                <Field.Select
                  name='notification_setting.repeatingInterval'
                  legend=''
                  options={REPEAT_NOTIFICATION_OPTION}
                  disabled={values?.notification_setting?.mute}
                />
              </Grid>
              <Grid item xs={6} md={4}>
                <Field.Select
                  name='notification_setting.url'
                  legend=''
                  options={soundOptions}
                  disabled={values?.notification_setting?.mute}
                />
              </Grid>
              <Grid item xs={2} md={2}>
                <PlayAudioButton
                  value={values?.notification_setting?.url}
                  disabled={values?.notification_setting?.mute}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Content>
      <Content>
        <Title>Preferences</Title>
        <Typography
          sx={{
            fontWeight: 'bold',
          }}
        >
          Set time zone
        </Typography>
        <RadioContainer
          name='timezoneType'
          onChange={(_, newValue) => {
            if (
              newValue === TIMEZONE_SETTING.MANUALLY &&
              values.timezone === TIMEZONE_SETTING.AUTO_DETECT
            ) {
              setFieldValue('timezone', DEFAULT_TIMEZONE);
            }
            if (newValue === TIMEZONE_SETTING.AUTO_DETECT) {
              setFieldValue('timezone', TIMEZONE_SETTING.AUTO_DETECT);
            }
          }}
          options={[
            {
              value: TIMEZONE_SETTING.AUTO_DETECT,
              label: (
                <div>
                  <TextTitle>Auto-detect time zone</TextTitle>
                  {values.timezoneType === TIMEZONE_SETTING.AUTO_DETECT && (
                    <RadioContent>
                      <RadioHighlight>{autoTimeZone}</RadioHighlight>
                    </RadioContent>
                  )}
                </div>
              ),
            },
            {
              value: TIMEZONE_SETTING.MANUALLY,
              label: (
                <div>
                  <TextTitle>Manually set a time zone</TextTitle>
                  {values.timezoneType === TIMEZONE_SETTING.MANUALLY && (
                    <RadioContent>
                      <Field.Select
                        name='timezone'
                        legend=''
                        options={timezoneOptions}
                        disabled={isLoadingTimezone}
                        renderOption={(option) => (
                          <MenuItem
                            sx={{
                              'display': 'flex',
                              'alignItems': 'center',
                              'justifyContent': 'space-between',
                              'gap': '8px',
                              '&.Mui-selected': {
                                backgroundColor: '#EDF0F7 !important',
                              },
                            }}
                            key={option?.value}
                            value={option?.value || ''}
                          >
                            <span>{option.label}</span>
                            <span>{option.description}</span>
                          </MenuItem>
                        )}
                        renderValue={(val) => {
                          if (!val) {
                            return null;
                          }
                          const option = timezoneOptions.find(
                            (o) => o?.value === val,
                          );
                          return (
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: '8px',
                              }}
                            >
                              <span>{option?.label}</span>
                              <span>{option?.description}</span>
                            </Box>
                          );
                        }}
                      />
                      <LocalTimeContainer>
                        <LocalTime timezone={values.timezone} />
                      </LocalTimeContainer>
                    </RadioContent>
                  )}
                </div>
              ),
            },
          ]}
        />
        <Divider sx={{ borderColor: '#E2E7F0', width: '100%' }} />
        <Typography
          sx={{
            fontWeight: 'bold',
          }}
        >
          List pagination
        </Typography>
        <Field.Select
          name='items_per_page'
          legend=''
          options={CHANGE_PAGE_OPTION}
        />
      </Content>
      <ChangePwdModal
        isOpen={isChangingPwd}
        onClose={() => setIsChangingPwd(false)}
      />
    </ContentContainer>
  );
};
